import type React from 'react';

export interface CalendarEvent {
  id: string;
  title: string;
  date: string; // ISO format date string (YYYY-MM-DD)
  startTime: string; // 24-hour format (HH:MM)
  endTime: string; // 24-hour format (HH:MM)
  category: 'work' | 'personal' | 'important' | 'meeting' | 'other';
  description?: string;
  readonly?: boolean; // When true, event cannot be dragged or resized
  isPreview?: boolean;
}

export const CALENDAR_VIEW_MODE = {
  DAY: 'day',
  WEEK: 'week',
} as const;

export type CalendarViewMode =
  (typeof CALENDAR_VIEW_MODE)[keyof typeof CALENDAR_VIEW_MODE];

export interface EventRenderProps {
  event: CalendarEvent;
  isDragging: boolean;
  isResizing: boolean;
  totalColumns: number;
}

export type EventRenderer = (props: EventRenderProps) => React.ReactNode;

export interface CalendarSchedulerProps {
  events?: CalendarEvent[];
  onEventCreate?: (event: Omit<CalendarEvent, 'id'>) => void;
  onEventUpdate?: (event: CalendarEvent) => void;
  onEventDelete?: (id: string) => void;
  initialDate?: Date;
  className?: string;
  onDateChange?: (date: Date) => void;
  onToday?: () => void;
  compact?: boolean;
  renderEvent?: EventRenderer;
  scrollToTime?: Date;
  dragSnapMinutes?: number;
  alwaysShowCurrentTime?: boolean;
  externalDropIdPrefix?: string;
  suppressSlotHover?: boolean;
  viewMode?: CalendarViewMode;
  onViewModeChange?: (mode: CalendarViewMode) => void;
  enableWeekView?: boolean;
  hideToolbar?: boolean;
}

export interface ToolbarProps {
  currentDate: Date;
  viewMode: CalendarViewMode;
  onPrevDay: () => void;
  onNextDay: () => void;
  onToday: () => void;
  onViewModeChange?: (mode: CalendarViewMode) => void;
  enableWeekView?: boolean;
}

export interface TimeSlotClickHandler {
  (hour: number, minute: number): void;
}

export interface EventResizeHandler {
  (event: CalendarEvent, newEndTime: string): void;
}

export interface EventDropData {
  eventId: string;
  event: CalendarEvent;
}
